import { useEffect, useState } from 'react';

const DEFAULT_PAGINATION = {
    total: 0,
    limit: 10,
    offset: 0,
    hasNext: false,
    hasPrev: false,
};

export const usePaginatedSearchList = ({
    token,
    getItems,
    searchItems,
    initialSearchField = 'code',
    initialStatusFilter = 'ALL',
    statusAllValue = 'ALL',
    initialRowsPerPage = 10,
    debounceMs = 600,
    searchFieldParam = 'field',
    searchValueParam = 'value',
    statusParam = 'status',
}) => {
    const [items, setItems] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');

    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
    const [searchField, setSearchField] = useState(initialSearchField);
    const [statusFilter, setStatusFilter] = useState(initialStatusFilter);
    const [rowsPerPage, setRowsPerPage] = useState(initialRowsPerPage);
    const [offset, setOffset] = useState(0);

    const [pagination, setPagination] = useState({
        ...DEFAULT_PAGINATION,
        limit: initialRowsPerPage,
    });
    const [lastResponse, setLastResponse] = useState(null);

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
        }, debounceMs);

        return () => clearTimeout(timeoutId);
    }, [searchTerm, debounceMs]);

    useEffect(() => {
        const fetchItems = async () => {
            if (!token) return;

            setIsLoading(true);
            setError('');

            const normalizedSearch = debouncedSearchTerm.trim();
            const requestParams = {
                limit: rowsPerPage,
                offset,
                ...(statusFilter !== statusAllValue ? { [statusParam]: statusFilter } : {}),
            };

            const response = normalizedSearch
                ? await searchItems(token, {
                    ...requestParams,
                    [searchFieldParam]: searchField,
                    [searchValueParam]: normalizedSearch,
                })
                : await getItems(token, requestParams);

            if (response.success) {
                const responseData = Array.isArray(response.data) ? response.data : [];

                setItems(responseData);
                setPagination({
                    total: Number(response.total) || 0,
                    limit: Number(response.limit) || rowsPerPage,
                    offset: Number(response.offset) || 0,
                    hasNext: Boolean(response.hasNext),
                    hasPrev: Boolean(response.hasPrev),
                });
                setLastResponse(response);
            } else {
                setItems([]);
                setPagination((prev) => ({
                    ...prev,
                    total: 0,
                    hasNext: false,
                    hasPrev: false,
                }));
                setLastResponse(null);
                setError(response.message || 'No se pudieron cargar los registros.');
            }

            setIsLoading(false);
        };

        fetchItems();
    }, [
        token,
        getItems,
        searchItems,
        rowsPerPage,
        offset,
        debouncedSearchTerm,
        searchField,
        statusFilter,
        statusAllValue,
        statusParam,
        searchFieldParam,
        searchValueParam,
    ]);

    const handleRowsPerPageChange = (nextRowsPerPage) => {
        setRowsPerPage(nextRowsPerPage);
        setOffset(0);
    };

    const handleSearchTermChange = (value) => {
        setSearchTerm(value);
        setOffset(0);
    };

    const handleSearchFieldChange = (value) => {
        setSearchField(value);
        setOffset(0);
    };

    const handleStatusFilterChange = (value) => {
        setStatusFilter(value);
        setOffset(0);
    };

    const handleClearFilters = () => {
        setSearchTerm('');
        setDebouncedSearchTerm('');
        setSearchField(initialSearchField);
        setStatusFilter(initialStatusFilter);
        setOffset(0);
    };

    return {
        items,
        isLoading,
        error,
        pagination,
        lastResponse,
        searchTerm,
        searchField,
        statusFilter,
        rowsPerPage,
        setOffset,
        handleRowsPerPageChange,
        handleSearchTermChange,
        handleSearchFieldChange,
        handleStatusFilterChange,
        handleClearFilters,
    };
};
